import { buildCanonicalFingerprint } from "@/lib/dismissal-fingerprints";
import { gameTimeMatcher } from "@/lib/game-date";
import { searchMatcher, type SearchMatcher } from "@/lib/odds-screen/live/derive";
import type { ArbitrageView, ArbLeg, ArbPick, ArbRow, EventHeader, RowId } from "@/lib/odds-screen/live/types";
import type { MarketScope } from "@/components/optimizer/query-state";
import type { ArbFilters } from "./filters";
import { displayOrder, marketLabel, middleWindow } from "./labels";
import { isPlusEV, legEV, planStakes, planTypedStakes, type StakePlan } from "./plan";

const EPSILON = 1e-9;

export type ArbPlay = {
  readonly row: ArbRow;
  readonly event: EventHeader;
  readonly market: string;
  readonly pick: ArbPick;
  readonly legs: readonly ArbLeg[];
  readonly evs: readonly (number | null)[];
  readonly plan: StakePlan;
  readonly window: string | null;
  readonly others: readonly ArbPick[];
};

export function isGameRow(row: Pick<ArbRow, "marketType">): boolean {
  return row.marketType === "game";
}

export function matchesScope(row: Pick<ArbRow, "marketType">, scope: MarketScope): boolean {
  return scope === "" || (scope === "game") === isGameRow(row);
}

export function matchesSearch(row: Pick<ArbRow, "entity" | "team" | "market">, event: EventHeader, matches: SearchMatcher | null): boolean {
  return matches === null || matches(row.entity.name, row.team, row.market, event.name);
}

export type HideTool = "arbitrage" | "lowhold";

export function hideFingerprint(row: Pick<ArbRow, "id" | "league" | "eventId" | "marketKey">, tool: HideTool): string | null {
  return buildCanonicalFingerprint({ sport: row.league, eventId: row.eventId, entityId: row.id, market: row.marketKey, line: null, side: `${tool}-pair` });
}

export function isHidden(row: Pick<ArbRow, "id" | "league" | "eventId" | "marketKey">, tool: HideTool, hidden: ReadonlySet<string>): boolean {
  if (hidden.size === 0) return false;
  const fingerprint = hideFingerprint(row, tool);
  return fingerprint !== null && hidden.has(fingerprint);
}

export function selectablePicks(row: ArbRow, selected: ReadonlySet<string>): ArbPick[] {
  return row.picks.filter((pick) => pick.legs.every((index) => selected.has(row.legs[index].book)));
}

export function bettableBooks(selected: ReadonlySet<string>, targetBooks: readonly string[]): ReadonlySet<string> {
  return targetBooks.every((book) => selected.has(book)) ? selected : new Set([...selected, ...targetBooks]);
}

export function hitsTarget(legs: readonly Pick<ArbLeg, "book">[], targetBooks: readonly string[]): boolean {
  return targetBooks.length === 0 || legs.some((leg) => targetBooks.includes(leg.book));
}

export function planPlay(row: ArbRow, event: EventHeader, pick: ArbPick, others: readonly ArbPick[], stake: number): ArbPlay {
  const picked = pick.legs.map((index) => row.legs[index]);
  const order = displayOrder(picked);
  const legs = order.map((k) => picked[k]);
  const market = marketLabel(row.market, legs);
  const plan = planStakes({
    shares: order.map((k) => pick.stakes[k]),
    odds: legs.map((leg) => leg.odds),
    limits: legs.map((leg) => leg.limit),
    maxStake: pick.maxStake,
    stake,
    middle: pick.middle,
  });
  return {
    row, event, market, pick, legs, plan, others,
    evs: legs.map((leg) => legEV(leg.odds, leg.fair)),
    window: pick.middle ? middleWindow(legs, market, event, row.league) : null,
  };
}

export type TypedLegStake = {
  readonly key: string;
  readonly amount: number;
};

export function legKey(leg: Pick<ArbLeg, "book" | "side" | "line">): string {
  return `${leg.book}:${leg.side}:${leg.line}`;
}

export function withTypedStakes(play: ArbPlay, typed: readonly TypedLegStake[]): ArbPlay {
  const matched = typed.flatMap((entry) => {
    const leg = play.legs.findIndex((each) => legKey(each) === entry.key);
    return leg === -1 ? [] : [{ leg, amount: entry.amount }];
  });
  if (matched.length === 0) return play;
  const plan = planTypedStakes({ odds: play.legs.map((leg) => leg.odds), limits: play.legs.map((leg) => leg.limit), middle: play.pick.middle }, matched);
  return { ...play, plan };
}

export function samePlay(a: ArbPlay, b: ArbPlay): boolean {
  return a.legs.length === b.legs.length
    && a.legs.every((leg, index) => {
      const other = b.legs[index];
      return leg.book === other.book && leg.side === other.side && leg.line === other.line && leg.odds === other.odds;
    })
    && a.plan.stakes.every((stake, index) => stake === b.plan.stakes[index]);
}

export type ArbFoldInput = {
  readonly view: ArbitrageView;
  readonly filters: ArbFilters;
  readonly selected: ReadonlySet<string>;
  readonly hidden: ReadonlySet<string>;
  readonly search: string;
  readonly stake: number;
  readonly nowMs?: number;
};

export type ArbSource = {
  readonly ids: readonly RowId[];
  readonly selectable: number;
  readonly rowAt: (id: RowId) => ArbPlay | null;
};

function compare(filters: ArbFilters): (a: ArbPlay, b: ArbPlay) => number {
  const sign = filters.order === "asc" ? 1 : -1;
  const flagged = (a: ArbPlay, b: ArbPlay) => Number(a.pick.check) - Number(b.pick.check);
  if (filters.sort === "profit") return (a, b) => flagged(a, b) || sign * (a.plan.profit - b.plan.profit) || b.pick.roi - a.pick.roi;
  return (a, b) => flagged(a, b) || sign * (a.pick.roi - b.pick.roi) || b.plan.profit - a.plan.profit;
}

function firstPlaceable(row: ArbRow, event: EventHeader, picks: readonly ArbPick[], filters: ArbFilters, stake: number): ArbPlay | null {
  for (const pick of [...picks.filter((each) => !each.check), ...picks.filter((each) => each.check)]) {
    if (pick.roi * 100 < filters.minReturn - EPSILON) continue;
    const play = planPlay(row, event, pick, picks.filter((other) => other !== pick), stake);
    if (filters.middlesOnly && play.window === null) continue;
    if (filters.evOnly && !play.evs.some(isPlusEV)) continue;
    if (!play.plan.placeable || play.plan.profit <= 0) continue;
    return play;
  }
  return null;
}

export function foldArbitrage({ view, filters, selected, hidden, search, stake, nowMs = Date.now() }: ArbFoldInput): ArbSource {
  const matches = searchMatcher(search);
  const startsIn = gameTimeMatcher({ days: filters.days, within: filters.within, live: false }, new Date(nowMs));
  const byId = new Map<RowId, ArbPlay>();
  const kept: ArbPlay[] = [];
  let selectable = 0;
  for (const id of view.rowIds) {
    const row = view.rows.get(id);
    const event = row === undefined ? undefined : view.events.get(row.eventId);
    if (row === undefined || event === undefined) continue;
    const picks = selectablePicks(row, selected);
    if (picks.length === 0) continue;
    selectable += 1;
    if (!matchesScope(row, filters.scope)) continue;
    if (startsIn !== null && !startsIn(event.startMs)) continue;
    if (!matchesSearch(row, event, matches) || isHidden(row, "arbitrage", hidden)) continue;
    const aimed = filters.targetBooks.length === 0 ? picks : picks.filter((pick) => hitsTarget(pick.legs.map((index) => row.legs[index]), filters.targetBooks));
    const play = firstPlaceable(row, event, aimed, filters, stake);
    if (play === null) continue;
    byId.set(id, play);
    kept.push(play);
  }
  kept.sort(compare(filters));
  return { ids: kept.map((play) => play.row.id), selectable, rowAt: (id) => byId.get(id) ?? null };
}
